import { useState } from "react";
import {
	FileText,
	Download,
	Clock,
	CheckCircle,
	Eye,
	X,
	Loader2,
} from "lucide-react";
import { useApp } from "../components/AppContext";
import { MainLayout } from "../components/MainLayout";
import { useContracts } from "../api/contracts/useContracts";
import { useDownloadContract } from "../api/contracts/useDownloadContract";
import { useCurrentUser } from "../api/auth/useCurrentUser";
import { AddressModal } from "../components/AddressModal";
import { parseEventDate } from "../utils/dateUtils";

export function ContractManagement() {
	const { navigateDirectly } = useApp();
	const { user, loading: userLoading } = useCurrentUser();
	const {
		contracts,
		loading: contractsLoading,
		error: contractsError,
		refetch,
	} = useContracts();
	const { downloading, downloadContract } = useDownloadContract();

	// PDF viewer state
	const [showPdfModal, setShowPdfModal] = useState(false);
	const [selectedContract, setSelectedContract] = useState<any>(null);
	const [pdfUrl, setPdfUrl] = useState<string | null>(null);
	const [loadingPdf, setLoadingPdf] = useState(false);

	// Address modal state
	const [showAddressModal, setShowAddressModal] = useState(false);
	const [pendingContractId, setPendingContractId] = useState<number | null>(
		null,
	);

	// Only show contracts for external users (internal_extra === false or undefined)
	const isExternalUser = user?.internal_extra === false;

	const getStatusIcon = (status: string) => {
		switch (status) {
			case "awaiting_download":
				return <Clock size={16} className="text-orange-500" />;
			case "viewed":
				return <Eye size={16} className="text-purple-500" />;
			case "downloaded":
				return <Download size={16} className="text-blue-500" />;
			case "completely_signed":
				return <FileText size={16} className="text-cyan-500" />;
			case "downloaded_signed":
				return <CheckCircle size={16} className="text-green-500" />;
			default:
				return <FileText size={16} className="text-gray-500" />;
		}
	};

	const getStatusLabel = (status: string) => {
		switch (status) {
			case "awaiting_download":
				return "Download ausstehend";
			case "viewed":
				return "Angesehen";
			case "downloaded":
				return "Heruntergeladen";
			case "completely_signed":
				return "Vollständig unterschrieben";
			case "downloaded_signed":
				return "Unterschriebenen Vertrag heruntergeladen";
			default:
				return "Unbekannt";
		}
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case "awaiting_download":
				return "text-orange-600 bg-orange-50 border-orange-200";
			case "viewed":
				return "text-purple-600 bg-purple-50 border-purple-200";
			case "downloaded":
				return "text-blue-600 bg-blue-50 border-blue-200";
			case "completely_signed":
				return "text-cyan-600 bg-cyan-50 border-cyan-200";
			case "downloaded_signed":
				return "text-green-600 bg-green-50 border-green-200";
			default:
				return "text-gray-600 bg-gray-50 border-gray-200";
		}
	};

	const formatDate = (dateString: string) => {
		const d = parseEventDate(dateString) || new Date(dateString);
		return d.toLocaleDateString("de-DE", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		});
	};

	const handleDownload = async (contractId: number) => {
		try {
			// First, check if user has address information
			const token = localStorage.getItem("accessToken");
			if (!token) {
				throw new Error("Keine Authentifizierung verfügbar");
			}

			const checkResponse = await fetch(
				`${import.meta.env.VITE_API_URL || "http://localhost:3001/api"}/contracts/${contractId}/check-address`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);

			if (!checkResponse.ok) {
				throw new Error("Fehler beim Prüfen der Adresse");
			}

			const addressData = await checkResponse.json();

			// If address is missing, show address modal
			if (!addressData.data.has_address) {
				setPendingContractId(contractId);
				setShowAddressModal(true);
				return;
			}

			// Address exists, proceed with download
			await downloadContract(contractId);
			// Refresh contracts to update status
			await refetch();
		} catch (error) {
			console.error("Error in handleDownload:", error);
			// Error is already handled by the hook
		}
	};

	const handleAddressSuccess = async () => {
		// Address saved and contract regenerated, now open preview
		setShowAddressModal(false);

		if (pendingContractId) {
			// Find the contract to pass to handleViewPdf
			const contract = contracts.find((c) => c.id === pendingContractId);
			if (contract) {
				// Open the preview modal
				await openPdfPreview(contract);
			}
		}

		setPendingContractId(null);
	};

	// Helper function to open PDF preview (without address check since we just saved it)
	const openPdfPreview = async (contract: any) => {
		try {
			const token = localStorage.getItem("accessToken");
			if (!token) {
				throw new Error("Keine Authentifizierung verfügbar");
			}

			setSelectedContract(contract);
			setLoadingPdf(true);
			setShowPdfModal(true);

			const response = await fetch(
				`${import.meta.env.VITE_API_URL || "http://localhost:3001/api"}/contracts/${contract.id}/view`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);

			if (!response.ok) {
				throw new Error(`Fehler beim Laden der PDF: ${response.statusText}`);
			}

			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			setPdfUrl(url);
		} catch (error: any) {
			console.error("Error loading PDF:", error);
			setShowPdfModal(false);
		} finally {
			setLoadingPdf(false);
		}
	};

	const handleAddressCancel = () => {
		setShowAddressModal(false);
		setPendingContractId(null);
	};

	// View PDF function
	const handleViewPdf = async (contract: any) => {
		if (!contract.has_pdf) {
			return;
		}

		try {
			const token = localStorage.getItem("accessToken");
			if (!token) {
				throw new Error("Keine Authentifizierung verfügbar");
			}

			// First, check if user has address information
			const checkResponse = await fetch(
				`${import.meta.env.VITE_API_URL || "http://localhost:3001"}/contracts/${contract.id}/check-address`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);

			if (!checkResponse.ok) {
				throw new Error("Fehler beim Prüfen der Adresse");
			}

			const addressData = await checkResponse.json();

			// If address is missing, show address modal
			if (!addressData.data.has_address) {
				setPendingContractId(contract.id);
				setShowAddressModal(true);
				return;
			}

			// Address exists, proceed with viewing
			setSelectedContract(contract);
			setLoadingPdf(true);
			setShowPdfModal(true);

			const response = await fetch(
				`${import.meta.env.VITE_API_URL || "http://localhost:3001/api"}/contracts/${contract.id}/view`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);

			if (!response.ok) {
				throw new Error(`Fehler beim Laden der PDF: ${response.statusText}`);
			}

			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			setPdfUrl(url);
		} catch (error: any) {
			console.error("Error loading PDF:", error);
			setShowPdfModal(false);
		} finally {
			setLoadingPdf(false);
		}
	};

	const handleClosePdfModal = () => {
		setShowPdfModal(false);
		setSelectedContract(null);
		if (pdfUrl) {
			window.URL.revokeObjectURL(pdfUrl);
			setPdfUrl(null);
		}
	};

	// Show loading state
	if (userLoading) {
		return (
			<MainLayout showBack={true} onBack={() => navigateDirectly("dashboard")}>
				<div className="px-6">
					<div className="flex items-center justify-center py-8">
						<div className="text-center">
							<div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
							<p className="text-[var(--ucm-text-medium)]">Lädt...</p>
						</div>
					</div>
				</div>
			</MainLayout>
		);
	}

	// Hide contracts section for internal users
	if (!isExternalUser) {
		return (
			<MainLayout showBack={true} onBack={() => navigateDirectly("dashboard")}>
				<div className="px-6">
					<div className="mb-6">
						<h1 className="text-2xl font-bold text-[var(--ucm-text-dark)] mb-2">
							Vertrag
						</h1>
						<p className="text-[var(--ucm-text-medium)]">
							Dieser Bereich ist nur für externe Nutzer verfügbar.
						</p>
					</div>
				</div>
			</MainLayout>
		);
	}

	return (
		<MainLayout showBack={true} onBack={() => navigateDirectly("dashboard")}>
			<div className="px-6">
				<div className="mb-6">
					<h1 className="text-2xl font-bold text-[var(--ucm-text-dark)] mb-2">
						Vertrag
					</h1>
					<p className="text-[var(--ucm-text-medium)]">
						Status Ihres Arbeitsvertrags
					</p>
				</div>

				{contractsLoading ? (
					<div className="flex items-center justify-center py-8">
						<div className="text-center">
							<div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
							<p className="text-[var(--ucm-text-medium)]">
								Verträge werden geladen...
							</p>
						</div>
					</div>
				) : contractsError ? (
					<div className="bg-red-50 border border-red-200 rounded-2xl p-5">
						<div className="flex items-center space-x-3">
							<FileText size={20} className="text-red-500" />
							<div>
								<h3 className="font-semibold text-red-700">
									Fehler beim Laden der Verträge
								</h3>
								<p className="text-sm text-red-600">{contractsError}</p>
							</div>
						</div>
					</div>
				) : contracts.length === 0 ? (
					<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
						<div className="flex items-center mb-4">
							<div className="w-8 h-8 bg-[var(--ucm-light-blue)] rounded-full flex items-center justify-center mr-3">
								<Clock size={16} className="text-[var(--ucm-dark-blue)]" />
							</div>
							<h3 className="font-semibold text-[var(--ucm-text-dark)]">
								Noch kein Vertrag verfügbar
							</h3>
						</div>

						<div className="mb-4">
							<div className="flex items-center justify-between mb-2">
								<span className="text-sm font-medium text-[var(--ucm-text-medium)]">
									Status:
								</span>
								<span className="text-sm font-semibold text-[var(--ucm-info-blue)]">
									Warten auf Buchung
								</span>
							</div>
							<p className="text-sm text-[var(--ucm-text-medium)] mb-3">
								Sobald Sie für einen Einsatz gebucht werden, erhalten Sie hier
								Ihren Vertrag.
							</p>

							<div className="bg-[var(--ucm-light-gray)] rounded-xl p-4 mb-4">
								<p className="text-sm text-[var(--ucm-text-dark)] leading-relaxed">
									Ihr Arbeitsvertrag wird automatisch hier verfügbar sein,
									sobald Sie für einen Einsatz gebucht wurden. Sie können den
									Vertrag dann hier herunterladen, um ihn unterschrieben per
									Post an uns zurückzusenden.
								</p>
							</div>

							<div className="bg-[var(--ucm-light-blue)] rounded-xl p-3">
								<p className="text-xs font-medium text-[var(--ucm-dark-blue)] mb-1">
									Wie geht es weiter?
								</p>
								<p className="text-xs text-[var(--ucm-dark-blue)]">
									Sobald Sie gebucht sind, wird hier Ihr Vertrag zum
									Herunterladen bereitstehen. Weitere Informationen zum Versand
									des unterschriebenen Vertrags erhalten Sie dann ebenfalls
									hier.
								</p>
							</div>
						</div>
					</div>
				) : (
					<div className="space-y-4">
						{/* Info Box - Contract Process */}
						<div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
							<h4 className="text-lg font-semibold text-blue-900 mb-4">
								So geht es weiter mit Ihrem Arbeitsvertrag:
							</h4>
							<div className="space-y-3 text-base text-blue-800">
								<div className="flex items-start space-x-3">
									<span className="flex-shrink-0 w-7 h-7 bg-blue-200 text-blue-900 rounded-full flex items-center justify-center text-sm font-semibold">
										1
									</span>
									<p>
										<span className="font-semibold">Vertrag herunterladen</span>{" "}
										- Laden Sie Ihren Arbeitsvertrag über den Button "Anzeigen"
										herunter
									</p>
								</div>
								<div className="flex items-start space-x-3">
									<span className="flex-shrink-0 w-7 h-7 bg-blue-200 text-blue-900 rounded-full flex items-center justify-center text-sm font-semibold">
										2
									</span>
									<p>
										<span className="font-semibold">
											Ausdrucken und unterschreiben
										</span>{" "}
										- Drucken Sie den Vertrag aus und unterschreiben Sie ihn
									</p>
								</div>
								<div className="flex items-start space-x-3">
									<span className="flex-shrink-0 w-7 h-7 bg-blue-200 text-blue-900 rounded-full flex items-center justify-center text-sm font-semibold">
										3
									</span>
									<p>
										<span className="font-semibold">Per Post senden</span> -
										Senden Sie den unterschriebenen Vertrag{" "}
										<span className="font-semibold">
											innerhalb von 2 Wochen
										</span>{" "}
										an folgende Adresse:{" "}
										<span className="font-semibold">
											Postfach 51 05 38, 13365 Berlin
										</span>
									</p>
								</div>
								<div className="flex items-start space-x-3">
									<span className="flex-shrink-0 w-7 h-7 bg-blue-200 text-blue-900 rounded-full flex items-center justify-center text-sm font-semibold">
										4
									</span>
									<p>
										<span className="font-semibold">
											Gegenzeichnung durch ucm
										</span>{" "}
										- Wir werden den Vertrag entgegennehmen, gegenzeichnen und
										Ihnen hier zur Verfügung stellen. Bitte beachten Sie, dass
										dieser Vorgang einige Tage in Anspruch nehmen kann.
									</p>
								</div>
							</div>
						</div>

						{/* Group contracts by event_id */}
						{(() => {
							const grouped = contracts.reduce<Record<number, typeof contracts>>((acc, contract) => {
								const key = contract.event_id;
								if (!acc[key]) acc[key] = [];
								acc[key].push(contract);
								return acc;
							}, {});

							return Object.entries(grouped).map(([eventId, eventContracts]) => {
								const mainContract = eventContracts.find(c => !c.assistant_id);
								const assistantContract = eventContracts.find(c => !!c.assistant_id);
								const hasAssistant = !!assistantContract;
								const displayDate = (mainContract || eventContracts[0]).event_date;

								return (
									<div key={eventId} className="bg-white rounded-2xl shadow-[0_4px_16px_rgba(0,0,0,0.08)] overflow-hidden">
										{/* Event header — only shown when both contracts exist */}
										{hasAssistant && (
											<div className="px-5 pt-4 pb-2 border-b border-gray-100">
												<p className="text-sm font-semibold text-[var(--ucm-text-dark)]">
													Termin: {formatDate(displayDate)}
												</p>
												<p className="text-xs text-blue-600 mt-1">
													Hier finden Sie sowohl Ihren als auch den Vertrag Ihrer Begleitung. Bitte unterschreiben
													Sie beide den Vertrag und senden uns diese zu.
												</p>
											</div>
										)}

										{/* Render each contract in the group */}
										{eventContracts.map((contract, idx) => (
											<div
												key={contract.id}
												className={`p-5 ${hasAssistant && idx < eventContracts.length - 1 ? "border-b border-dashed border-gray-200" : ""}`}
											>
												<div>
													<div className="flex items-start space-x-3">
														<div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${contract.assistant_id ? "bg-purple-50" : "bg-blue-50"}`}>
															<FileText size={20} className={contract.assistant_id ? "text-purple-600" : "text-blue-600"} />
														</div>
														<div className="flex-1 min-w-0">
															<h3 className="font-semibold text-[var(--ucm-text-dark)] mb-1">
																{contract.assistant_id
																	? "Arbeitsvertrag Begleitung"
																	: contract.event_name === "external" ||
																	  contract.event_name === "internal"
																		? "Ihr Arbeitsvertrag"
																		: contract.event_name}
															</h3>
															{!hasAssistant && (
																<p className="text-sm text-[var(--ucm-text-medium)] mb-2">
																	Termin: {formatDate(contract.event_date)}
																</p>
															)}
															<div className="flex flex-wrap gap-2">
																<div
																	className={`inline-flex items-center space-x-1 px-2 py-1 rounded-lg border text-xs font-medium ${getStatusColor(contract.status)}`}
																>
																	{getStatusIcon(contract.status)}
																	<span>{getStatusLabel(contract.status)}</span>
																</div>
																{contract.status === "completely_signed" && (
																	<div className="inline-flex items-center space-x-1 px-2 py-1 rounded-lg border text-xs font-medium text-red-700 bg-red-50 border-red-200">
																		<CheckCircle size={12} className="text-red-600" />
																		<span>UCM-signiert</span>
																	</div>
																)}
															</div>
														</div>
													</div>
													<div className="mt-3">
														{contract.has_pdf ? (
															<button
																onClick={() => handleViewPdf(contract)}
																className="h-10 w-full bg-blue-600 text-white font-medium rounded-full hover:bg-blue-700 transition-colors duration-200 text-sm flex items-center justify-center space-x-2"
															>
																<Eye size={16} />
																<span>Anzeigen</span>
															</button>
														) : (
															<span className="text-xs text-gray-500">
																Keine PDF verfügbar
															</span>
														)}
													</div>
												</div>
											</div>
										))}
									</div>
								);
							});
						})()}
					</div>
				)}

				{/* Address Modal */}
				{showAddressModal && pendingContractId && (
					<AddressModal
						contractId={pendingContractId}
						onClose={handleAddressCancel}
						onSuccess={handleAddressSuccess}
					/>
				)}

				{/* PDF Viewer Modal */}
				{showPdfModal && (
					<div
						className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 sm:p-4"
						onClick={handleClosePdfModal}
					>
						<div
							className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-4xl max-h-[95vh] sm:max-h-[90vh] flex flex-col"
							onClick={(e) => e.stopPropagation()}
						>
							{/* Modal Header */}
							<div className="flex items-center justify-between p-4 sm:p-6 border-b">
								<div className="flex items-center space-x-3">
									<div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
										<FileText size={16} className="text-blue-600" />
									</div>
									<div>
										<h3 className="text-lg font-semibold text-[var(--ucm-text-dark)]">
											Vertrags-PDF
										</h3>
										{selectedContract && (
											<p className="text-base text-[var(--ucm-text-medium)]">
												{selectedContract.event_name === "external" ||
												selectedContract.event_name === "internal"
													? "Ihr Arbeitsvertrag"
													: selectedContract.event_name}{" "}
												- {formatDate(selectedContract.event_date)}
											</p>
										)}
									</div>
								</div>
								<button
									onClick={handleClosePdfModal}
									className="p-2 hover:bg-gray-100 rounded-full transition-colors"
								>
									<X size={20} className="text-gray-500" />
								</button>
							</div>

							{/* Modal Body */}
							<div className="flex-1 p-3 sm:p-6 overflow-hidden">
								{loadingPdf ? (
									<div className="flex flex-col items-center justify-center h-60 sm:h-96">
										<Loader2
											size={48}
											className="animate-spin text-blue-600 mb-4"
										/>
										<p className="text-[var(--ucm-text-medium)]">
											PDF wird geladen...
										</p>
									</div>
								) : pdfUrl ? (
									<>
										{/* Warning above PDF */}
										{(selectedContract?.status === "awaiting_download" ||
											selectedContract?.status === "viewed" ||
											selectedContract?.status === "downloaded") && (
											<div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
												<p className="text-base text-amber-800 font-semibold">
													Wichtig: Bitte senden Sie den Vertrag innerhalb von 2
													Wochen an Postfach 51 05 38, 13365 Berlin
												</p>
											</div>
										)}
										<iframe
											src={`${pdfUrl}#toolbar=0`}
											className="w-full h-[60vh] sm:h-96 border rounded-lg"
											title={`Vertrag - ${selectedContract?.event_name}`}
										/>
									</>
								) : (
									<div className="flex flex-col items-center justify-center h-96">
										<FileText size={48} className="text-gray-400 mb-4" />
										<p className="text-[var(--ucm-text-medium)]">
											Fehler beim Laden der PDF
										</p>
									</div>
								)}
							</div>

							{/* Modal Footer */}
							{selectedContract && (
								<div className="p-4 sm:p-6 border-t bg-gray-50 rounded-b-2xl">
									{/* Status and Download Section */}
									<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
										<div className="flex items-center space-x-4">
											<div className="flex flex-col space-y-2">
												<div
													className={`inline-flex items-center space-x-1 px-2 py-1 rounded-lg border text-xs font-medium ${getStatusColor(selectedContract.status)}`}
												>
													{getStatusIcon(selectedContract.status)}
													<span>{getStatusLabel(selectedContract.status)}</span>
												</div>
												{selectedContract.status === "completely_signed" && (
													<div className="inline-flex items-center space-x-1 px-2 py-1 rounded-lg border text-xs font-medium text-red-700 bg-red-50 border-red-200 w-fit">
														<CheckCircle size={12} className="text-red-600" />
														<span>UCM-signierter Vertrag verfügbar</span>
													</div>
												)}
											</div>
										</div>
										<button
											onClick={() => handleDownload(selectedContract.id)}
											disabled={downloading}
											className="h-10 w-full sm:w-auto px-4 bg-blue-600 text-white font-medium rounded-full hover:bg-blue-700 transition-colors duration-200 text-sm flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
										>
											{downloading ? (
												<>
													<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
													<span>Lädt...</span>
												</>
											) : (
												<>
													<Download size={16} />
													<span>Herunterladen</span>
												</>
											)}
										</button>
									</div>

									{/* Next Steps Instructions */}
									{(selectedContract.status === "awaiting_download" ||
										selectedContract.status === "viewed" ||
										selectedContract.status === "downloaded") && (
										<div className="bg-white border border-amber-200 rounded-lg p-4">
											<h4 className="text-base font-semibold text-gray-800 mb-4">
												Wichtige Schritte nach dem Download:
											</h4>
											<div className="space-y-3 text-base text-gray-700">
												<div className="flex items-start space-x-3">
													<span className="flex-shrink-0 w-7 h-7 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold">
														1
													</span>
													<p>
														<span className="font-semibold">
															Vertrag herunterladen
														</span>{" "}
														- Klicken Sie auf den Herunterladen-Button oben
													</p>
												</div>
												<div className="flex items-start space-x-3">
													<span className="flex-shrink-0 w-7 h-7 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold">
														2
													</span>
													<p>
														<span className="font-semibold">
															Ausdrucken und unterschreiben
														</span>{" "}
														- Drucken Sie den Vertrag aus und unterschreiben Sie
														ihn an den markierten Stellen
													</p>
												</div>
												<div className="flex items-start space-x-3">
													<span className="flex-shrink-0 w-7 h-7 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold">
														3
													</span>
													<p>
														<span className="font-semibold">
															Per Post senden
														</span>{" "}
														- Senden Sie den unterschriebenen Vertrag an
														folgende Adresse:{" "}
														<span className="font-semibold">
															Postfach 51 05 38, 13365 Berlin
														</span>
													</p>
												</div>
												<div className="flex items-start space-x-3">
													<span className="flex-shrink-0 w-7 h-7 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold">
														4
													</span>
													<p>
														<span className="font-semibold">
															Gegenzeichnung durch ucm
														</span>{" "}
														- Wir werden den Vertrag entgegennehmen,
														gegenzeichnen und Ihnen hier zur Verfügung stellen.
														Bitte beachten Sie, dass dieser Vorgang einige Tage
														in Anspruch nehmen kann.
													</p>
												</div>
											</div>
											<div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
												<p className="text-base text-amber-800 font-semibold">
													Wichtig: Bitte senden Sie den Vertrag innerhalb von 2
													Wochen an Postfach 51 05 38, 13365 Berlin
												</p>
											</div>
										</div>
									)}
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</MainLayout>
	);
}
